import React, { useState } from 'react';
import { Dialog } from '@blueprintjs/core';
import { Button, TextAreaLimited, TextInput } from 'components';
import { useMutation } from '@apollo/client';
import { RESOLVE_DATA_FIX_MUTATION } from 'gql';
import { useModal } from 'providers/modalProvider';
import { useToast } from 'providers/toastProvider';
import { useIntl } from 'react-intl';
import { path } from 'ramda';
import { buildActionSummary } from 'utils/dataFixSummary';

const ResolveDataFixModal = () => {
  const { modalDispatch, modalState } = useModal();
  const { showToast } = useToast();
  const { formatMessage } = useIntl();
  const [targetId, setTargetId] = useState('');
  const [notes, setNotes] = useState('');

  const { row, refetch } = path(['payload'], modalState) ?? {};
  const needsTarget = !row?.targetId;

  const closeModal = () => modalDispatch({ type: 'CLOSE_ALL' });

  const [resolve, { loading }] = useMutation(RESOLVE_DATA_FIX_MUTATION, {
    onCompleted: () => {
      showToast({
        message: formatMessage({
          id: 'containers.dataFixes.modals.resolve.success',
        }),
      });
      closeModal();
      refetch?.();
    },
    onError: () =>
      showToast({
        message: formatMessage({ id: 'containers.all.error' }),
        icon: 'error',
      }),
  });

  const handleResolve = () =>
    resolve({
      variables: {
        input: {
          id: row.id,
          ...(needsTarget && targetId ? { targetId } : {}),
          notes: notes || undefined,
        },
      },
    });

  return (
    <Dialog
      isOpen={modalState?.open}
      title={formatMessage({ id: 'containers.dataFixes.modals.resolve.title' })}
      onClose={closeModal}
      canOutsideClickClose={false}
    >
      <div className="bp3-dialog-body">
        <p>
          {formatMessage({ id: 'containers.dataFixes.modals.resolve.confirm' })}
        </p>
        <p>
          <strong>{buildActionSummary(row)}</strong>
        </p>
        {needsTarget && (
          <TextInput
            labelText={formatMessage({
              id: 'containers.dataFixes.modals.resolve.targetId',
            })}
            placeholder={formatMessage({
              id: 'containers.dataFixes.modals.resolve.targetId.placeholder',
            })}
            value={targetId}
            onChange={setTargetId}
            required
            className="mgt-10"
          />
        )}
        <TextAreaLimited
          onChange={(e) => setNotes(e.target.value)}
          value={notes}
          placeholder={formatMessage({
            id: 'containers.dataFixes.modals.resolve.notes',
          })}
          maxLength="500"
          showMaxLength
          className="mgt-10"
        />
      </div>
      <div className="bp3-dialog-footer-actions">
        <Button
          outlined
          text={formatMessage({ id: 'containers.all.btn.cancel' })}
          onClick={closeModal}
          disabled={loading}
        />
        <Button
          intent="Success"
          text={formatMessage({ id: 'containers.dataFixes.actions.resolve' })}
          onClick={handleResolve}
          loading={loading}
          disabled={needsTarget && !targetId}
        />
      </div>
    </Dialog>
  );
};

export default ResolveDataFixModal;
